import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  fetchPipelinesAndVersionSearch,
  pipelinesAndVersionQueryKeys,
  type PipelinesAndVersionSearchResponse
} from '~/api/monitorService';
import type { AsyncAutocompleteItem } from '~/components/AsyncAutocomplete';

const AUTOCOMPLETE_PAGE_SIZE = 10;

type ParsedVersion = {
  major: number | undefined;
  minor: number | undefined;
};

function parseVersionString(input: string): ParsedVersion {
  const trimmed = input.trim();
  if (!trimmed) return { major: undefined, minor: undefined };

  const dotIndex = trimmed.indexOf('.');
  if (dotIndex === -1) {
    const major = parseInt(trimmed, 10);
    return { major: Number.isNaN(major) ? undefined : major, minor: undefined };
  }

  const majorStr = trimmed.slice(0, dotIndex);
  const minorStr = trimmed.slice(dotIndex + 1);
  const major = parseInt(majorStr, 10);
  const minor = parseInt(minorStr, 10);

  return {
    major: Number.isNaN(major) ? undefined : major,
    minor: Number.isNaN(minor) ? undefined : minor
  };
}

function toAutocompleteItems(strings: string[]): AsyncAutocompleteItem[] {
  return strings.map(s => ({ key: s, label: s }));
}

export type UsePipelinesAndVersionAutocompleteParams = {
  realm: string;
  enabled?: boolean;
  selectedPipelineName?: string | null;
};

export type UsePipelinesAndVersionAutocompleteResult = {
  pipelineItems: AsyncAutocompleteItem[];
  versionItems: AsyncAutocompleteItem[];
  isPipelineLoading: boolean;
  isVersionLoading: boolean;
  isPipelineError: boolean;
  isVersionError: boolean;
  pipelineHasMore: boolean;
  versionHasMore: boolean;
  versionValue: null;
  onPipelineSearch: (query: string) => void;
  onVersionSearch: (query: string) => void;
  onPipelineLoadMore: () => void;
  onVersionLoadMore: () => void;
};

function isWildcard(name: string | null | undefined): boolean {
  return !!name && (name.startsWith('*') || name.endsWith('*'));
}

export function usePipelinesAndVersionAutocomplete({
  realm,
  enabled = true,
  selectedPipelineName
}: UsePipelinesAndVersionAutocompleteParams): UsePipelinesAndVersionAutocompleteResult {
  const [pipelineQuery, setPipelineQuery] = useState('');
  const [versionQuery, setVersionQuery] = useState('');

  const exactPipelineName =
    !selectedPipelineName || isWildcard(selectedPipelineName)
      ? undefined
      : selectedPipelineName;

  useEffect(() => {
    setVersionQuery('');
  }, [exactPipelineName]);

  const parsedVersion = useMemo(
    () => parseVersionString(versionQuery),
    [versionQuery]
  );

  const {
    data: pipelineData,
    isLoading: isPipelineLoading,
    isError: isPipelineError,
    hasNextPage: pipelineHasNextPage,
    fetchNextPage: pipelineFetchNextPage
  } = useInfiniteQuery<PipelinesAndVersionSearchResponse, Error>({
    queryKey: pipelinesAndVersionQueryKeys.search({
      realm,
      query: pipelineQuery.replace(/^\*|\*$/g, ''),
      returnType: 'pipeline'
    }),
    queryFn: ({ pageParam }) =>
      fetchPipelinesAndVersionSearch({
        realm,
        pipelineName: pipelineQuery.replace(/^\*|\*$/g, '') || undefined,
        returnType: 'pipeline',
        page: (pageParam as number) ?? 1,
        limit: AUTOCOMPLETE_PAGE_SIZE
      }),
    getNextPageParam: lastPage => {
      const { total, page, limit } = lastPage.pagination;
      return page * limit < total ? page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  const {
    data: versionData,
    isLoading: isVersionLoading,
    isError: isVersionError,
    hasNextPage: versionHasNextPage,
    fetchNextPage: versionFetchNextPage
  } = useInfiniteQuery<PipelinesAndVersionSearchResponse, Error>({
    queryKey: pipelinesAndVersionQueryKeys.search({
      realm,
      query: versionQuery,
      returnType: 'version',
      pipelineName: exactPipelineName,
      versionMajor: parsedVersion.major,
      versionMinor: parsedVersion.minor
    }),
    queryFn: ({ pageParam }) =>
      fetchPipelinesAndVersionSearch({
        realm,
        pipelineName: exactPipelineName,
        versionMajor: parsedVersion.major,
        versionMinor: parsedVersion.minor,
        returnType: 'version',
        page: (pageParam as number) ?? 1,
        limit: AUTOCOMPLETE_PAGE_SIZE
      }),
    getNextPageParam: lastPage => {
      const { total, page, limit } = lastPage.pagination;
      return page * limit < total ? page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  const pipelineItems = useMemo(
    () =>
      toAutocompleteItems(pipelineData?.pages.flatMap(page => page.data) ?? []),
    [pipelineData]
  );

  const versionItems = useMemo(
    () =>
      toAutocompleteItems(versionData?.pages.flatMap(page => page.data) ?? []),
    [versionData]
  );

  const onPipelineSearch = useCallback((query: string) => {
    setPipelineQuery(query);
  }, []);

  const onVersionSearch = useCallback((query: string) => {
    setVersionQuery(query);
  }, []);

  const onPipelineLoadMore = useCallback(() => {
    if (pipelineHasNextPage) {
      pipelineFetchNextPage();
    }
  }, [pipelineHasNextPage, pipelineFetchNextPage]);

  const onVersionLoadMore = useCallback(() => {
    if (versionHasNextPage) {
      versionFetchNextPage();
    }
  }, [versionHasNextPage, versionFetchNextPage]);

  return {
    pipelineItems,
    versionItems,
    isPipelineLoading,
    isVersionLoading,
    isPipelineError,
    isVersionError,
    pipelineHasMore: pipelineHasNextPage ?? false,
    versionHasMore: versionHasNextPage ?? false,
    versionValue: null,
    onPipelineSearch,
    onVersionSearch,
    onPipelineLoadMore,
    onVersionLoadMore
  };
}
